import { Stack } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridValueGetterParams
} from '@mui/x-data-grid-pro';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { TwButton, TwButtonType } from '../../../../components/TwButtons/TwButtons';
import TwDataGrid, { elementRenderBasic } from '../../../../components/TwDataGrid/TwDataGrid';
import TwGenericMenu from '../../../../components/TwGenericMenu/TwGenericMenu';
import SalesTaxService, { SalesTax } from '../../../../providers/Settings/SalesTax.service';
import CreateUpdateModal from './CreateUpdateModal';

interface SalesTaxSettingsProps {
  divisionId: string;
}

export const SalesTaxSettings: FC<SalesTaxSettingsProps> = ({ divisionId }) => {
  const [salesTaxSettings, setSalesTaxSettings] = useState<SalesTax[]>([]);
  const [isCreateUpdateModalOpen, setIsCreateUpdateModalOpen] = useState<boolean>(false);

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'NAME',
      sortable: false,
      renderCell: elementRenderBasic,
      width: 250,
    },
    {
      field: 'tax_id',
      headerName: 'TAX ITEM ID',
      sortable: false,
      renderCell: elementRenderBasic,
      width: 250,
    },
    {
      field: 'rate',
      headerName: 'RATE',
      sortable: false,
      renderCell: elementRenderBasic,
      width: 250,
      valueGetter: (params: GridValueGetterParams<any>): string => {
        return `${params.value}%`;
      }
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      width: 50,
      renderCell: (params: GridRenderCellParams): ReactNode => {
        return (
          <TwGenericMenu
            className="sales-tax-settings__options-three-dots"
            options={[
              {
                label: params.row.is_active ? 'Disable' : 'Enable',
                action: (): void => {
                  SalesTaxService.updatePartialSalesTax(params.row.id, {
                    is_active: !params.row.is_active
                  }).then(() => {
                    refetchSalesTaxSettings();
                  });
                }
              },
              {
                label: 'Delete',
                className: 'sales-tax-settings__options-three-dots__delete',
                action: (): void => {
                  SalesTaxService.deleteSalesTax(params.row.id).then(() => {
                    refetchSalesTaxSettings();
                  });
                }
              }
            ]}
          />
        );
      },
      resizable: false,
      align: 'right'
    }
  ];

  useEffect(() => {
    refetchSalesTaxSettings();
  }, [divisionId]);

  const handleAddSalesTax = (): void => {
    setIsCreateUpdateModalOpen(true);
  };

  const handleCloseCreateUpdateModal = (doCreate: boolean): void => {
    setIsCreateUpdateModalOpen(false);
    if (doCreate) {
      refetchSalesTaxSettings();
    }
  };

  const refetchSalesTaxSettings = (): void => {
    SalesTaxService.getSalesTaxSettings(divisionId, { sort: 'is_active' }).then((settings) => {
      setSalesTaxSettings(settings);
    });
  };

  return (
    <Stack>
      <CreateUpdateModal
        open={isCreateUpdateModalOpen}
        onClose={handleCloseCreateUpdateModal}
        divisionId={divisionId}
      />
      <TwDataGrid
        columns={columns}
        rows={salesTaxSettings}
        getRowClassName={(params: GridRowParams): string => {
          return params.row.is_active ? '' : 'sales-tax-settings__sales-tax-inactive';
        }}
        hideFooter
        disableRowSelectionOnClick
      />
      <TwButton
        label="+ Add tax"
        type={TwButtonType.text}
        sx={{ display: 'inline-block', textAlign: 'left' }}
        action={(): void => {
          handleAddSalesTax();
        }}
      />
    </Stack>
  );
};

export default SalesTaxSettings;
